import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountService } from '../../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { CustomerEntryPass } from '../../../../../../../../../models/entry-pass';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { Customer, CustomerFilters } from '../../../../../../../../../models/customer';
import { Subject } from 'rxjs';
import { ListResponse } from '../../../../../../../../../models/list-response';
import { dateStringFromISO8601 } from '../../../../../../../../../utils';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-link-customer-entry-pass-modal',
  templateUrl: './link-customer-entry-pass-modal.component.html',
  styleUrls: ['./link-customer-entry-pass-modal.component.css']
})
export class LinkCustomerEntryPassModalComponent implements OnInit {

  @Input() entryPass: CustomerEntryPass;
  @Input() savedCallback: (successMessage: string) => undefined;

  public loadingSubmit = false;

  public serverError: number = null;

  public errors: any = {};
  public errorMessage = '';

  public loadingCustomers = false;
  public customerFilters: CustomerFilters = {
    search: ''
  };
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponse<Customer>;
  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    private accountService: AccountService,
    private customerService: CustomerService,
    public orgConfig: OrganizationConfigService,
    public activeModal: NgbActiveModal
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    if (this.entryPass.owner_association_id) {
      this.customerFilters.has_association_memberships = this.entryPass.owner_association_id.toString();
    }

    // Create a deep copy of the entry pass so we don't edit the parent list.
    // Note: this only works as long as no nested objects are edited.
    const entryPassCopy = {};
    for (const key of Object.keys(this.entryPass)) {
      entryPassCopy[key] = this.entryPass[key];
    }
    this.entryPass = entryPassCopy as CustomerEntryPass;

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  public removeCustomer(customerId: number) {
    // We assign the lists rather than editing them in place so the parent pass
    // is not edited.
    this.entryPass.all_customer_ids = this.entryPass.all_customer_ids.filter(
      (thisId) => (thisId !== customerId)
    );
    this.entryPass.all_customers = this.entryPass.all_customers.filter(
      (thisCustomer) => (thisCustomer.id !== customerId)
    );
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (this.customerFilters.search === '') {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .getCustomers(
        1,
        // 51 allows us to still display 50 results if we need to remove the base
        // customer from the list.
        51,
        this.customerFilters,
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.filteredCustomerResults().length > 0)
    );
  }

  public filteredCustomerResults(): Customer[] {
    return this.customerList.results.filter(
      (customer) => (this.entryPass.all_customer_ids.indexOf(customer.id) === -1)
    );
  }

  /**
   * Handle a click on one of the customer results by adding it to the linked
   * customers.
   */
  public selectRelatedCustomer(customer: Customer) {
    // We assign the lists rather than editing them in place so the parent pass
    // is not edited.
    this.entryPass.all_customer_ids = this.entryPass.all_customer_ids.concat([customer.id]);
    this.entryPass.all_customers = this.entryPass.all_customers.concat([{
      id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name
    }]);
  }

  public handleSave() {
    this.loadingSubmit = true;
    this.errors = {};
    this.errorMessage = '';

    this.customerService
      .updateCustomerEntryPassLinkedCustomers(this.entryPass)
      .then(() => {
        this.savedCallback('Entry pass linked successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to link this pass.';
        } else if (reason.status === 400) {
          this.errorMessage = 'Please correct the errors above and try again.';
          this.errors = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public saveEnabled() {
    return (this.entryPass.all_customer_ids.length > 0) || (this.entryPass.owner_association_id);
  }

}
